import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const InstitutionDashboard = ({ user, onLogout }) => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/institution/dashboard`);
      setDashboardData(response.data);
    } catch (error) {
      toast.error('Failed to load dashboard');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const copyKey = () => {
    if (dashboardData?.institution?.institution_key) {
      navigator.clipboard.writeText(dashboardData.institution.institution_key);
      toast.success('Institution Key copied to clipboard!');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-100">
      <header className="bg-white/80 backdrop-blur-md border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Institution Dashboard</h1>
                <p className="text-sm text-gray-600">{dashboardData?.institution?.name}</p>
              </div>
            </div>
            <Button onClick={onLogout} variant="outline" className="border-gray-300 hover:bg-gray-100" data-testid="logout-button">
              Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Institution Key Card */}
        <Card className="p-6 bg-white/90 backdrop-blur-sm border-0 shadow-lg mb-8">
          <h2 className="text-lg font-semibold mb-4">Your Institution Key</h2>
          <div className="bg-emerald-50 border-2 border-emerald-200 rounded-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-2">Share this key with supervisors to register:</p>
                <code className="text-3xl font-bold text-emerald-700" data-testid="institution-key">
                  {dashboardData?.institution?.institution_key}
                </code>
              </div>
              <button
                onClick={copyKey}
                className="p-4 hover:bg-emerald-100 rounded-lg transition-colors"
                data-testid="copy-key-button"
              >
                <svg className="w-6 h-6 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </button>
            </div>
          </div>
        </Card>

        {/* Stats Card */}
        <Card className="p-6 bg-white/90 backdrop-blur-sm border-0 shadow-lg mb-8">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Registered Supervisors</p>
              <p className="text-4xl font-bold text-emerald-600">{dashboardData?.supervisor_count || 0}</p>
            </div>
            <div className="w-16 h-16 bg-emerald-100 rounded-xl flex items-center justify-center">
              <svg className="w-8 h-8 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
          </div>
        </Card>

        {/* Institution Details */}
        <Card className="p-6 bg-white/90 backdrop-blur-sm border-0 shadow-lg mb-8">
          <h2 className="text-lg font-semibold mb-4">Institution Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-600">Contact Phone</p>
              <p className="font-medium">{dashboardData?.institution?.phone}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Email</p>
              <p className="font-medium">{dashboardData?.institution?.email}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Address</p>
              <p className="font-medium">{dashboardData?.institution?.street_address}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">City</p>
              <p className="font-medium">{dashboardData?.institution?.city}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Country</p>
              <p className="font-medium">{dashboardData?.institution?.country}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Status</p>
              <Badge className="bg-green-100 text-green-700">{dashboardData?.institution?.status}</Badge>
            </div>
          </div>
        </Card>

        {/* Supervisors List */}
        <Card className="p-6 bg-white/90 backdrop-blur-sm border-0 shadow-lg">
          <h2 className="text-lg font-semibold mb-4">Registered Supervisors</h2>
          {dashboardData?.supervisors?.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <p>No supervisors registered yet</p>
              <p className="text-sm mt-2">Share your Institution Key with supervisors to get started</p>
            </div>
          ) : (
            <div className="space-y-3">
              {dashboardData?.supervisors.map((supervisor) => (
                <Card key={supervisor.id} className="p-4 border border-gray-200">
                  <div className="flex justify-between items-start">
                    <div>
                      <h4 className="font-semibold text-gray-900">{supervisor.profile?.name || 'N/A'}</h4>
                      <p className="text-sm text-gray-600">{supervisor.email}</p>
                      <p className="text-xs text-gray-400 mt-1">Registered: {new Date(supervisor.created_at).toLocaleDateString()}</p>
                    </div>
                    <Badge className="bg-teal-100 text-teal-700">{supervisor.status}</Badge>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default InstitutionDashboard;